import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Palette,
  Table2,
  FileText,
  BarChart3,
  Link2,
  Code2,
  Play,
  Pause,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  FileImage,
  Layers,
  Type,
  Image,
  QrCode,
  Star,
  GripVertical,
  ArrowRight,
  Mail,
  Clock,
  Sparkles,
  Wand2,
  PenTool,
  Download,
  Eye,
  Lock,
  RefreshCw,
} from "lucide-react";
import "./LandingDemo.css";

/* ── Demo scenario definitions ── */

interface DemoStep {
  title: string;
  description: string;
  screen: React.ReactNode;
}

interface DemoScenario {
  id: string;
  label: string;
  icon: React.FC<{ className?: string }>;
  steps: DemoStep[];
  comingSoon?: boolean;
}

/* ── Mockup screens for each step ── */

/* Scenario 1: Personalized Outreach */
const outreachSteps: DemoStep[] = [
  {
    title: "Import prospect data into SnapSheet",
    description:
      "Paste or upload a CSV of prospect names, companies, and emails. SnapSheet is your built-in spreadsheet for managing all personalization data.",
    screen: (
      <div className="demo-screen-snapsheet">
        <div className="demo-toolbar">
          <div className="demo-toolbar-btn active">
            <Table2 className="w-3.5 h-3.5" /> SnapSheet
          </div>
          <div className="demo-toolbar-btn">
            <Palette className="w-3.5 h-3.5" /> Editor
          </div>
        </div>
        <div className="demo-spreadsheet">
          <div className="demo-row demo-row-header">
            <div className="demo-cell demo-cell-idx">#</div>
            <div className="demo-cell">first_name</div>
            <div className="demo-cell">company</div>
            <div className="demo-cell">email</div>
            <div className="demo-cell">logo_url</div>
          </div>
          {[
            ["Sarah", "Acme Corp", "sarah@acme.co", "acme.co/logo.png"],
            ["James", "TechFlow", "james@techflow.io", "techflow.io/logo.png"],
            ["Maria", "BrightStar", "maria@bright.co", "bright.co/logo.png"],
            ["Alex", "CloudPeak", "alex@cloudpeak.io", "cloudpeak.io/logo.png"],
          ].map((row, i) => (
            <div key={i} className={`demo-row ${i === 0 ? "demo-row-active" : ""}`}>
              <div className="demo-cell demo-cell-idx">{i + 1}</div>
              {row.map((val, j) => (
                <div key={j} className="demo-cell">
                  {val}
                </div>
              ))}
            </div>
          ))}
          <div className="demo-row demo-row-count">
            <span>4 rows · 4 columns · Ready to personalize</span>
          </div>
        </div>
      </div>
    ),
  },
  {
    title: "Design your template in the Editor",
    description:
      "Use the visual editor to create your outreach image. Add text, images, shapes, QR codes, and star ratings. Drag and drop layers, snap to guides, and preview in real time.",
    screen: (
      <div className="demo-screen-editor">
        <div className="demo-toolbar">
          <div className="demo-toolbar-btn">
            <Table2 className="w-3.5 h-3.5" /> SnapSheet
          </div>
          <div className="demo-toolbar-btn active">
            <Palette className="w-3.5 h-3.5" /> Editor
          </div>
        </div>
        <div className="demo-editor-layout">
          {/* Sidebar */}
          <div className="demo-editor-sidebar">
            <div className="demo-sidebar-section">
              <span className="demo-sidebar-label">Layers</span>
              {[
                { icon: Type, name: "{{first_name}}", color: "purple" },
                { icon: Type, name: "{{company}}", color: "purple" },
                { icon: Image, name: "Logo", color: "blue" },
                { icon: FileImage, name: "Background", color: "slate" },
                { icon: QrCode, name: "QR Code", color: "emerald" },
              ].map((layer, i) => (
                <div key={i} className="demo-layer-item">
                  <GripVertical className="w-3 h-3 text-slate-600" />
                  <layer.icon className={`w-3 h-3 text-${layer.color}-400`} />
                  <span className="text-[10px] text-slate-300 truncate">{layer.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Canvas */}
          <div className="demo-editor-canvas">
            <div className="demo-canvas-card">
              <div className="demo-canvas-bg" />
              <div className="demo-canvas-text demo-canvas-text-name">
                {"{{first_name}}"}
              </div>
              <div className="demo-canvas-text demo-canvas-text-company">
                {"{{company}}"}
              </div>
              <div className="demo-canvas-logo">
                <Image className="w-5 h-5 text-slate-500" />
              </div>
              <div className="demo-canvas-qr">
                <QrCode className="w-4 h-4 text-slate-400" />
              </div>
              {/* Selection handles */}
              <div className="demo-selection-box">
                <div className="demo-handle demo-handle-tl" />
                <div className="demo-handle demo-handle-tr" />
                <div className="demo-handle demo-handle-bl" />
                <div className="demo-handle demo-handle-br" />
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    title: "Preview personalized variations",
    description:
      "SnapLayer generates a unique image for each row in your SnapSheet. Preview any variation instantly — names, logos, and data are swapped in real time.",
    screen: (
      <div className="demo-screen-preview">
        <div className="demo-toolbar">
          <div className="demo-toolbar-btn active">
            <Eye className="w-3.5 h-3.5" /> Preview
          </div>
          <div className="demo-toolbar-btn">
            <Download className="w-3.5 h-3.5" /> Export
          </div>
        </div>
        <div className="demo-preview-grid">
          {[
            { name: "Sarah", company: "Acme Corp", gradient: "from-purple-600 to-blue-600" },
            { name: "James", company: "TechFlow", gradient: "from-blue-600 to-cyan-600" },
            { name: "Maria", company: "BrightStar", gradient: "from-emerald-600 to-teal-600" },
            { name: "Alex", company: "CloudPeak", gradient: "from-orange-600 to-red-600" },
          ].map((item, i) => (
            <div key={i} className="demo-preview-card">
              <div className={`demo-preview-image bg-gradient-to-br ${item.gradient}`}>
                <span className="text-xs font-bold text-white/90">
                  Hey {item.name}!
                </span>
                <span className="text-[9px] text-white/70">{item.company}</span>
              </div>
              <div className="flex items-center justify-between px-1.5 py-1">
                <span className="text-[9px] text-slate-400">{item.name}</span>
                <CheckCircle2 className="w-3 h-3 text-emerald-400" />
              </div>
            </div>
          ))}
        </div>
        <div className="demo-preview-footer">
          <span className="text-[10px] text-emerald-400">✓ 4 of 4 variations generated</span>
          <span className="text-[10px] text-slate-500">PNG · 1200×630</span>
        </div>
      </div>
    ),
  },
  {
    title: "Generate SnapLinks for tracking",
    description:
      "Each prospect gets a unique SnapLink — a personalized landing page URL. Track opens, clicks, and engagement in real time from your dashboard.",
    screen: (
      <div className="demo-screen-snaplinks">
        <div className="demo-toolbar">
          <div className="demo-toolbar-btn active">
            <Link2 className="w-3.5 h-3.5" /> SnapLinks
          </div>
        </div>
        <div className="demo-links-list">
          {[
            { name: "Sarah", slug: "sarah-acme", views: 3, clicks: 1 },
            { name: "James", slug: "james-techflow", views: 7, clicks: 4 },
            { name: "Maria", slug: "maria-bright", views: 2, clicks: 0 },
            { name: "Alex", slug: "alex-cloudpeak", views: 0, clicks: 0 },
          ].map((link, i) => (
            <div key={i} className="demo-link-row">
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-200 font-medium">{link.name}</div>
                <div className="text-[10px] text-slate-500 font-mono truncate">
                  snap.link/{link.slug}
                </div>
              </div>
              <div className="flex items-center gap-3 text-[10px]">
                <span className="text-slate-400">
                  <Eye className="w-3 h-3 inline mr-0.5" />
                  {link.views}
                </span>
                <span className="text-purple-400">
                  <ArrowRight className="w-3 h-3 inline mr-0.5" />
                  {link.clicks}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    ),
  },
];

/* Scenario 2: Client Reports */
const reportSteps: DemoStep[] = [
  {
    title: "Connect data sources",
    description:
      "Link your Google Analytics 4, Search Console, and DataForSEO accounts. SnapLayer pulls live data automatically — no spreadsheet exports needed.",
    screen: (
      <div className="demo-screen-connectors">
        <div className="demo-toolbar">
          <div className="demo-toolbar-btn active">
            <BarChart3 className="w-3.5 h-3.5" /> Data Connectors
          </div>
        </div>
        <div className="demo-connectors-grid">
          {[
            { name: "Google Analytics 4", status: "connected", color: "emerald" },
            { name: "Google Search Console", status: "connected", color: "emerald" },
            { name: "DataForSEO", status: "connected", color: "emerald" },
            { name: "Google Business Profile", status: "available", color: "slate" },
          ].map((c, i) => (
            <div key={i} className="demo-connector-card">
              <div className="flex items-center gap-2 mb-1.5">
                <div className={`w-2 h-2 rounded-full bg-${c.color}-400`} />
                <span className="text-xs text-slate-200 font-medium">{c.name}</span>
              </div>
              <span
                className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                  c.status === "connected"
                    ? "bg-emerald-500/10 text-emerald-400"
                    : "bg-slate-700 text-slate-500"
                }`}
              >
                {c.status === "connected" ? "Connected" : "Coming Soon"}
              </span>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    title: "Choose a report template",
    description:
      "Select from pre-built report templates or design your own. Each template maps to data connector fields — traffic, rankings, backlinks, and more.",
    screen: (
      <div className="demo-screen-templates">
        <div className="demo-toolbar">
          <div className="demo-toolbar-btn active">
            <FileText className="w-3.5 h-3.5" /> Report Templates
          </div>
        </div>
        <div className="demo-template-grid">
          {[
            { name: "Monthly SEO Report", pages: 8, icon: BarChart3 },
            { name: "Campaign Performance", pages: 5, icon: BarChart3 },
            { name: "Keyword Rankings", pages: 4, icon: BarChart3 },
          ].map((t, i) => (
            <div key={i} className={`demo-template-card ${i === 0 ? "demo-template-selected" : ""}`}>
              <div className="demo-template-thumb">
                <t.icon className="w-6 h-6 text-slate-400" />
              </div>
              <div className="text-xs text-slate-200 font-medium">{t.name}</div>
              <div className="text-[9px] text-slate-500">{t.pages} pages</div>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    title: "Schedule automated delivery",
    description:
      "Set up recurring report delivery — weekly or monthly. Reports are generated with live data and emailed automatically to each client.",
    screen: (
      <div className="demo-screen-schedule">
        <div className="demo-toolbar">
          <div className="demo-toolbar-btn active">
            <Clock className="w-3.5 h-3.5" /> Delivery Schedule
          </div>
        </div>
        <div className="demo-schedule-config">
          <div className="demo-schedule-row">
            <span className="text-xs text-slate-400">Frequency</span>
            <span className="text-xs text-white px-2 py-0.5 bg-purple-500/20 border border-purple-500/30 rounded">
              Monthly — 1st Monday
            </span>
          </div>
          <div className="demo-schedule-row">
            <span className="text-xs text-slate-400">Format</span>
            <span className="text-xs text-white">Branded PDF</span>
          </div>
          <div className="demo-schedule-row">
            <span className="text-xs text-slate-400">Delivery</span>
            <span className="text-xs text-white flex items-center gap-1">
              <Mail className="w-3 h-3" /> Email to client
            </span>
          </div>
          <div className="demo-schedule-row">
            <span className="text-xs text-slate-400">Next run</span>
            <span className="text-xs text-emerald-400">Jul 7, 2025 · 9:00 AM</span>
          </div>
          <div className="demo-schedule-status">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-emerald-300">Schedule active · 12 clients enrolled</span>
          </div>
        </div>
      </div>
    ),
  },
];

/* Scenario 3: SnapDocs (Documents + Signing) */
const docSteps: DemoStep[] = [
  {
    title: "Build a proposal from template",
    description:
      "Design proposals and contracts in the visual editor. Add text fields, signature blocks, and dynamic variables that personalize for each client.",
    screen: (
      <div className="demo-screen-editor">
        <div className="demo-toolbar">
          <div className="demo-toolbar-btn active">
            <FileText className="w-3.5 h-3.5" /> SnapDocs
          </div>
          <div className="demo-toolbar-btn">
            <PenTool className="w-3.5 h-3.5" /> Sign
          </div>
        </div>
        <div className="demo-doc-layout">
          <div className="demo-doc-page">
            <div className="text-xs font-bold text-slate-200 mb-1">Marketing Agency Proposal</div>
            <div className="text-[9px] text-slate-400 mb-3">Prepared for {"{{company}}"}</div>
            <div className="space-y-2">
              <div className="h-2 bg-slate-700/50 rounded w-full" />
              <div className="h-2 bg-slate-700/50 rounded w-4/5" />
              <div className="h-2 bg-slate-700/50 rounded w-full" />
              <div className="h-2 bg-slate-700/50 rounded w-3/5" />
            </div>
            <div className="mt-4 pt-3 border-t border-slate-700/30 space-y-2">
              <div className="flex items-center gap-2">
                <PenTool className="w-3 h-3 text-blue-400" />
                <div className="text-[9px] text-blue-400 border-b border-dashed border-blue-400/40 px-2 py-0.5">
                  Signature: {"{{client_name}}"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <PenTool className="w-3 h-3 text-purple-400" />
                <div className="text-[9px] text-purple-400 border-b border-dashed border-purple-400/40 px-2 py-0.5">
                  Signature: Agency Owner
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    title: "Route for multi-party signing",
    description:
      "Define a signing workflow with roles — reviewer, signer, approver. Each party receives a link, signs electronically, and the next step triggers automatically.",
    screen: (
      <div className="demo-screen-signing">
        <div className="demo-toolbar">
          <div className="demo-toolbar-btn">
            <FileText className="w-3.5 h-3.5" /> SnapDocs
          </div>
          <div className="demo-toolbar-btn active">
            <PenTool className="w-3.5 h-3.5" /> Signing Workflow
          </div>
        </div>
        <div className="demo-signing-flow">
          {[
            { role: "Reviewer", name: "Sarah — Account Manager", status: "Completed", done: true },
            { role: "Signer", name: "James — Client CEO", status: "Signed", done: true },
            { role: "Approver", name: "You — Agency Owner", status: "Awaiting", done: false },
          ].map((step, i) => (
            <div key={i} className="demo-signing-step">
              <div className="demo-signing-icon">
                {step.done ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 border-blue-400 animate-pulse" />
                )}
              </div>
              <div className="flex-1">
                <div className="text-[10px] text-slate-300 font-medium">{step.role}</div>
                <div className="text-[9px] text-slate-500">{step.name}</div>
              </div>
              <span
                className={`text-[9px] ${step.done ? "text-emerald-400" : "text-blue-400"}`}
              >
                {step.status}
              </span>
            </div>
          ))}
          <div className="demo-signing-audit">
            <Lock className="w-3 h-3 text-slate-500" />
            <span className="text-[9px] text-slate-500">
              Full audit trail · Timestamps · IP addresses · Legal consent
            </span>
          </div>
        </div>
      </div>
    ),
  },
];

/* Scenario 4: Coming Soon — AI Tools */
const aiSteps: DemoStep[] = [
  {
    title: "Generate images with AI",
    description:
      "Type a prompt and generate unique images directly inside the editor. Powered by DALL·E 3 and Stable Diffusion via Straico — bring your own API key.",
    screen: (
      <div className="demo-screen-ai">
        <div className="demo-toolbar">
          <div className="demo-toolbar-btn active">
            <Wand2 className="w-3.5 h-3.5" /> AI Generate
          </div>
        </div>
        <div className="demo-ai-panel">
          <div className="demo-ai-prompt">
            <span className="text-[10px] text-slate-400">Prompt</span>
            <div className="demo-ai-input">
              Modern abstract background with purple and blue gradients for a tech company...
            </div>
          </div>
          <div className="demo-ai-results">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="demo-ai-thumb">
                <Sparkles className="w-4 h-4 text-purple-400/60" />
              </div>
            ))}
          </div>
          <div className="text-[9px] text-purple-300 text-center mt-2">
            DALL·E 3 · 4 variations · BYOK (your API key)
          </div>
        </div>
      </div>
    ),
  },
  {
    title: "Enhance copy with AI text",
    description:
      "Select any text layer and use AI to rewrite, expand, or condense. GPT-4o-mini refines your headlines, CTAs, and body copy in one click.",
    screen: (
      <div className="demo-screen-ai">
        <div className="demo-toolbar">
          <div className="demo-toolbar-btn active">
            <Sparkles className="w-3.5 h-3.5" /> AI Text
          </div>
        </div>
        <div className="demo-ai-text">
          <div className="demo-ai-original">
            <span className="text-[9px] text-slate-500 uppercase">Original</span>
            <p className="text-xs text-slate-400">We help businesses grow with digital marketing</p>
          </div>
          <ArrowRight className="w-4 h-4 text-purple-400 mx-auto" />
          <div className="demo-ai-enhanced">
            <span className="text-[9px] text-purple-400 uppercase">AI Enhanced</span>
            <p className="text-xs text-white font-medium">
              Scale Your Revenue with Data-Driven Marketing
            </p>
          </div>
          <div className="demo-ai-actions">
            <span className="demo-ai-action">Rewrite</span>
            <span className="demo-ai-action">Expand</span>
            <span className="demo-ai-action">Condense</span>
            <span className="demo-ai-action">Persuade</span>
          </div>
        </div>
      </div>
    ),
  },
];

/* Scenario 5: Coming Soon — PDF Tools */
const pdfSteps: DemoStep[] = [
  {
    title: "Import PSD and personalize",
    description:
      "Import Adobe Photoshop files with all layers preserved. Add dynamic variables to any text layer. Export personalized versions as PNG, PDF, or back to PSD.",
    screen: (
      <div className="demo-screen-psd">
        <div className="demo-toolbar">
          <div className="demo-toolbar-btn active">
            <Layers className="w-3.5 h-3.5" /> PSD Import
          </div>
        </div>
        <div className="demo-psd-layers">
          {[
            { name: "Text: {{headline}}", type: "text", visible: true },
            { name: "Logo Layer", type: "image", visible: true },
            { name: "Background Fill", type: "shape", visible: true },
            { name: "Shadow Effect", type: "effect", visible: false },
          ].map((layer, i) => (
            <div key={i} className="demo-psd-layer">
              <Eye className={`w-3 h-3 ${layer.visible ? "text-slate-300" : "text-slate-600"}`} />
              <Layers className="w-3 h-3 text-slate-500" />
              <span className="text-[10px] text-slate-300 flex-1">{layer.name}</span>
              <span className="text-[9px] text-slate-600">{layer.type}</span>
            </div>
          ))}
          <div className="demo-psd-status">
            <RefreshCw className="w-3 h-3 text-blue-400" />
            <span className="text-[9px] text-blue-400">
              design-template.psd · 4 layers imported
            </span>
          </div>
        </div>
      </div>
    ),
  },
];

/* ── Assemble all scenarios ── */

const DEMO_SCENARIOS: DemoScenario[] = [
  { id: "outreach", label: "Personalize", icon: Palette, steps: outreachSteps },
  { id: "reports", label: "Reports", icon: BarChart3, steps: reportSteps },
  { id: "documents", label: "SnapDocs", icon: FileText, steps: docSteps },
  { id: "ai", label: "AI Tools", icon: Sparkles, steps: aiSteps, comingSoon: false },
  { id: "psd", label: "PSD Import", icon: Layers, steps: pdfSteps, comingSoon: false },
];

/* ── Main Demo Component ── */

const LandingDemo: React.FC = () => {
  const [activeScenario, setActiveScenario] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const mountedRef = useRef(true);

  const scenario = DEMO_SCENARIOS[activeScenario];

  const advanceStep = useCallback(() => {
    if (!mountedRef.current) return;
    setActiveStep((prev) => {
      const max = DEMO_SCENARIOS[activeScenario].steps.length - 1;
      if (prev >= max) {
        setActiveScenario((s) => (s + 1) % DEMO_SCENARIOS.length);
        return 0;
      }
      return prev + 1;
    });
  }, [activeScenario]);

  useEffect(() => {
    if (!autoPlay) return;
    const id = setInterval(advanceStep, 6000);
    return () => clearInterval(id);
  }, [autoPlay, advanceStep]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const selectScenario = (i: number) => {
    setActiveScenario(i);
    setActiveStep(0);
  };

  const goBack = () => {
    setAutoPlay(false);
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    } else {
      const prevScenario = (activeScenario - 1 + DEMO_SCENARIOS.length) % DEMO_SCENARIOS.length;
      setActiveScenario(prevScenario);
      setActiveStep(DEMO_SCENARIOS[prevScenario].steps.length - 1);
    }
  };

  const goForward = () => {
    setAutoPlay(false);
    const max = scenario.steps.length - 1;
    if (activeStep < max) {
      setActiveStep(activeStep + 1);
    } else {
      setActiveScenario((activeScenario + 1) % DEMO_SCENARIOS.length);
      setActiveStep(0);
    }
  };

  const currentStep = scenario.steps[activeStep];

  return (
    <div className="demo-container">
      {/* Scenario tabs */}
      <div className="demo-scenario-tabs">
        {DEMO_SCENARIOS.map((s, i) => (
          <button
            key={s.id}
            onClick={() => selectScenario(i)}
            className={`demo-scenario-tab ${i === activeScenario ? "active" : ""}`}
          >
            <s.icon className="w-4 h-4" />
            <span>{s.label}</span>
            {s.comingSoon && (
              <span className="demo-coming-soon-badge">Soon</span>
            )}
          </button>
        ))}
      </div>

      {/* Main demo area */}
      <div className="demo-main">
        {/* Step sidebar */}
        <div className="demo-steps-sidebar">
          {scenario.steps.map((step, i) => (
            <button
              key={i}
              onClick={() => {
                setActiveStep(i);
                setAutoPlay(false);
              }}
              className={`demo-step-item ${i === activeStep ? "active" : ""}`}
            >
              <span
                className={`demo-step-number ${
                  i === activeStep
                    ? "bg-sl-primary text-white"
                    : i < activeStep
                    ? "bg-emerald-500/20 text-emerald-400"
                    : "bg-slate-700 text-slate-500"
                }`}
              >
                {i < activeStep ? "✓" : i + 1}
              </span>
              <div className="flex-1 min-w-0 text-left">
                <div className="text-xs font-medium text-slate-200 leading-tight">
                  {step.title}
                </div>
              </div>
            </button>
          ))}

          {/* Controls */}
          <div className="demo-controls">
            <button onClick={goBack} className="demo-control-btn" title="Previous">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setAutoPlay(!autoPlay)}
              className="demo-control-btn"
              title={autoPlay ? "Pause" : "Play"}
            >
              {autoPlay ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
            <button onClick={goForward} className="demo-control-btn" title="Next">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* "Browser chrome" screen */}
        <div className="demo-browser-frame">
          <div className="demo-browser-topbar">
            <div className="demo-browser-dots">
              <span className="demo-dot demo-dot-red" />
              <span className="demo-dot demo-dot-yellow" />
              <span className="demo-dot demo-dot-green" />
            </div>
            <div className="demo-browser-url">
              <Lock className="w-3 h-3 text-emerald-400" />
              <span>app.snaplayer.app</span>
            </div>
          </div>
          <div className="demo-browser-content" key={`${activeScenario}-${activeStep}`}>
            {currentStep?.screen}
          </div>
          <div className="demo-browser-description">
            <p className="text-sm text-slate-300 leading-relaxed">
              {currentStep?.description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingDemo;
